import { useState, useEffect, useRef, useMemo } from "react";
import "./TransactionFeedback.scss";
import reddickImage from "../../../assets/reddickrednobackground.png";

// Sound imports
import successSound from "./sounds/success1.mp4";
import depositSound1 from "./sounds/deposit1.mp4";
import depositSound2 from "./sounds/deposit2.mp4";

/**
 * TransactionFeedback Component - SPECTACULAR EDITION 🎉
 * Ultra fun animations for mint/deposit success!
 * Optimized for mobile performance
 */
const TransactionFeedback = ({ isVisible, variant = "mint", onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [shakeScreen, setShakeScreen] = useState(false);
  const audioRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Detect mobile device for performance optimization
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || 
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  // Config based on variant
  const config = {
    mint: {
      text: "MINTED!",
      subtext: "💰 CHA-CHING! 💰",
      colorClass: "mint-theme",
      sounds: [successSound],
      emojis: ["💰", "🤑", "💵", "🏆", "⭐", "✨", "🎯", "🔥"],
    },
    deposit: {
      text: "DEPOSITED!",
      subtext: "🏦 SECURED! 🏦",
      colorClass: "deposit-theme",
      sounds: [depositSound1, depositSound2],
      emojis: ["🏦", "💎", "🔐", "📈", "🚀", "💫", "🌟", "🎊"],
    },
  };

  const currentConfig = config[variant] || config.mint;

  // Clear all timeouts
  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  // Add timeout helper
  const addTimeout = (callback, delay) => {
    const id = setTimeout(callback, delay);
    timeoutsRef.current.push(id);
    return id;
  };

  // Play sound
  const playSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      const soundSrc = variant === "deposit" 
        ? currentConfig.sounds[Math.floor(Math.random() * currentConfig.sounds.length)]
        : currentConfig.sounds[0];
      
      audioRef.current = new Audio(soundSrc);
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch(() => {});
    } catch (err) {}
  };

  // Stop sound
  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  // Main animation sequence
  useEffect(() => {
    if (isVisible) {
      // Reset all states
      setIsActive(true);
      setShowText(false);
      setShowFlash(false);
      setShowConfetti(false);
      setShowEmojis(false);
      setIsFading(false);
      setShakeScreen(false);

      // Phase 1: BOOM! Flash + Sound + Shake (at 400ms)
      addTimeout(() => {
        setShowFlash(true);
        if (!isMobile) setShakeScreen(true); // No shake on mobile - causes janky performance
        playSound();
      }, 400);

      // Phase 2: Hide flash, show confetti (at 600ms)
      addTimeout(() => {
        setShowFlash(false);
        setShowConfetti(true);
        if (!isMobile) setShowEmojis(true); // Fewer elements on mobile
      }, 600);

      // Phase 3: Stop shake, show text (at 800ms)
      addTimeout(() => {
        setShakeScreen(false);
        setShowText(true);
      }, 800);

      // Phase 4: Start fade out (at 2500ms)
      addTimeout(() => {
        setIsFading(true);
      }, 2500);

      // Phase 5: Complete (at 3000ms)
      addTimeout(() => {
        setIsActive(false);
        setShowText(false);
        setShowConfetti(false);
        setShowEmojis(false);
        setIsFading(false);
        stopSound();
        if (onComplete) {
          onComplete();
        }
      }, 3000);
    }

    return () => {
      clearAllTimeouts();
      stopSound();
    };
  }, [isVisible, isMobile]);

  // Generate confetti pieces - REDUCED for mobile
  const generateConfetti = (count) => {
    const actualCount = isMobile ? Math.min(count, 25) : count;
    const colors = variant === "mint" 
      ? ['#00ff88', '#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffbe0b']
      : ['#4a6bff', '#8a4bff', '#ff6b6b', '#00d4ff', '#ff00ff', '#00ff88', '#ffbe0b'];
    
    return Array.from({ length: actualCount }, (_, i) => {
      const left = Math.random() * 100;
      const delay = Math.random() * 0.5;
      const rotation = Math.random() * 720 - 360;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() > 0.5 ? 'large' : 'small';
      const shape = Math.random() > 0.7 ? 'rect' : Math.random() > 0.5 ? 'circle' : 'triangle';
      
      return (
        <div
          key={i}
          className={`confetti ${size} ${shape}`}
          style={{
            '--left': `${left}%`,
            '--delay': `${delay}s`,
            '--rotation': `${rotation}deg`,
            '--color': color,
            '--duration': `${1.5 + Math.random()}s`,
          }}
        />
      );
    });
  };

  // Generate burst particles - REDUCED for mobile
  const generateBurstParticles = (count) => {
    const actualCount = isMobile ? Math.min(count, 12) : count;
    return Array.from({ length: actualCount }, (_, i) => {
      const angle = (i / actualCount) * 360;
      const distance = isMobile ? 60 + Math.random() * 100 : 100 + Math.random() * 200;
      const delay = Math.random() * 0.2;
      const size = isMobile ? 4 + Math.random() * 6 : 6 + Math.random() * 10;
      
      return (
        <div
          key={i}
          className="burst-particle"
          style={{
            '--angle': `${angle}deg`,
            '--distance': `${distance}px`,
            '--delay': `${delay}s`,
            '--size': `${size}px`,
          }}
        />
      );
    });
  };

  // Generate flying emojis - DISABLED on mobile for performance
  const generateEmojis = (count) => {
    if (isMobile) return null; // Skip emojis on mobile
    
    return Array.from({ length: count }, (_, i) => {
      const emoji = currentConfig.emojis[Math.floor(Math.random() * currentConfig.emojis.length)];
      const left = 10 + Math.random() * 80;
      const delay = Math.random() * 0.8;
      const size = 24 + Math.random() * 24;
      const rotation = Math.random() * 60 - 30;
      
      return (
        <div
          key={i}
          className="flying-emoji"
          style={{
            '--left': `${left}%`,
            '--delay': `${delay}s`,
            '--size': `${size}px`,
            '--rotation': `${rotation}deg`,
          }}
        >
          {emoji}
        </div>
      );
    });
  };

  // Generate sparkle stars - REDUCED for mobile
  const generateSparkles = (count) => {
    const actualCount = isMobile ? Math.min(count, 8) : count;
    return Array.from({ length: actualCount }, (_, i) => (
      <div
        key={i}
        className="sparkle-star"
        style={{
          '--x': `${Math.random() * 100}%`,
          '--y': `${Math.random() * 100}%`,
          '--delay': `${Math.random() * 1}s`,
          '--size': `${10 + Math.random() * 15}px`,
        }}
      >
        ✦
      </div>
    ));
  };

  if (!isActive && !isVisible) return null;

  // Number of rings - REDUCED for mobile
  const ringCount = isMobile ? 4 : 8;

  return (
    <div className={`transaction-feedback ${currentConfig.colorClass} ${isActive ? 'active' : ''} ${isFading ? 'fading' : ''} ${shakeScreen ? 'shaking' : ''} ${isMobile ? 'mobile' : ''}`}>
      {/* Flash overlay */}
      <div className={`flash-overlay ${showFlash ? 'visible' : ''}`} />
      
      {/* Rainbow burst background - Simplified on mobile */}
      {!isMobile && <div className="rainbow-burst" />}
      
      {/* Animated rings - FEWER on mobile */}
      <div className="rings-container">
        {[...Array(ringCount)].map((_, i) => (
          <div key={i} className={`ring ring-${i + 1}`} style={{ '--delay': `${i * 0.15}s` }} />
        ))}
      </div>

      {/* Mascot with crazy animation */}
      <div className="mascot-wrapper">
        <div className="mascot-container">
          <img src={reddickImage} alt="Success" className="mascot main-mascot" />
        </div>
        {/* Extra bouncing mini mascots - HIDDEN on mobile */}
        {!isMobile && (
          <>
            <div className="mini-mascot mini-1">
              <img src={reddickImage} alt="" />
            </div>
            <div className="mini-mascot mini-2">
              <img src={reddickImage} alt="" />
            </div>
          </>
        )}
        <div className="mascot-glow" />
        {!isMobile && <div className="mascot-glow glow-2" />}
      </div>

      {/* Burst particles - REDUCED on mobile */}
      <div className="burst-container">
        {generateBurstParticles(40)}
      </div>

      {/* Confetti rain - REDUCED on mobile */}
      {showConfetti && (
        <div className="confetti-container">
          {generateConfetti(80)}
        </div>
      )}

      {/* Flying emojis - SKIPPED on mobile */}
      {showEmojis && !isMobile && (
        <div className="emoji-container">
          {generateEmojis(20)}
        </div>
      )}

      {/* Success text */}
      <div className={`success-text-wrapper ${showText ? 'visible' : ''}`}>
        <h1 className="success-text">
          {currentConfig.text.split('').map((char, i) => (
            <span key={i} style={{ '--i': i }}>{char}</span>
          ))}
        </h1>
        <p className="sub-text">{currentConfig.subtext}</p>
      </div>

      {/* Sparkle stars - REDUCED on mobile */}
      <div className="sparkles-container">
        {showText && generateSparkles(25)}
      </div>

      {/* Corner fireworks - HIDDEN on mobile */}
      {!isMobile && (
        <>
          <div className="firework firework-1" />
          <div className="firework firework-2" />
          <div className="firework firework-3" />
          <div className="firework firework-4" />
        </>
      )}
    </div>
  );
};

export default TransactionFeedback;